import { RiskLevel, LikelihoodColorLevel } from '../models/risk-assessment.model';

type ColorLevel = RiskLevel;

const DEFAULT_SCORE_COLOR = 'rgba(55, 55, 55, 0.4)';

/**
 * Internal helper that finds the index of the level whose threshold range a score falls within.
 * A score above the highest range clamps to the last level (mirrors the colour/name behaviour).
 * @param score - The score to evaluate (already validated as finite by the caller)
 * @param levels - Array of level configurations
 * @param getThresholds - Extracts the [min, max] threshold pair from a level. Callers vary this
 *   so the same matching logic stays shared (e.g. colour matching falls back to QualThresholds,
 *   name matching uses Thresholds only).
 * @returns The matching level index, or -1 if none matches
 */
function findMatchingLevelIndex(
    score: number,
    levels: ColorLevel[],
    getThresholds: (level: ColorLevel) => unknown,
): number {
    for (let i = 0; i < levels.length; i++) {
        const thresholds = getThresholds(levels[i]);
        if (!Array.isArray(thresholds) || thresholds.length < 2) continue;
        const min = Number(thresholds[0]);
        const max = Number(thresholds[1]);
        if (!Number.isFinite(min) || !Number.isFinite(max)) continue;
        if ((score >= min && score <= max) || (i === levels.length - 1 && score > max)) {
            return i;
        }
    }

    return -1;
}

/**
 * Internal helper to get color for a score within a set of levels
 * @param score - The score to evaluate
 * @param levels - Array of level configurations with Color and Thresholds
 * @param treatZeroAsNull - Whether to treat 0 as a null/invalid score (default: true for risk, false for likelihood)
 * @returns Color string for the score
 */
function getColorForScore(score: number, levels: ColorLevel[], treatZeroAsNull = true): string {
    if (!levels?.length || !Number.isFinite(score) || (treatZeroAsNull && score === 0)) {
        return DEFAULT_SCORE_COLOR;
    }

    const index = findMatchingLevelIndex(score, levels, (level) => {
        const l = level as ColorLevel & { QualThresholds?: [number, number] };
        return Array.isArray(l.Thresholds) ? l.Thresholds : l.QualThresholds;
    });
    if (index === -1) return DEFAULT_SCORE_COLOR;

    const matched = levels[index] as ColorLevel & { color?: string };
    return matched.Color ?? matched.color ?? DEFAULT_SCORE_COLOR;
}

export function getRiskColor(riskRating: number, riskLevels: RiskLevel[]): string {
    return getColorForScore(riskRating, riskLevels, true);
}

/**
 * Resolves the configured range name (e.g. "High") that a risk score falls under.
 * Returns null when there is no valid/analysed score or no matching range, so callers
 * can render an N/A fallback instead of a misleading raw value.
 *
 * NOTE: Range matching only considers the quantitative `level.Thresholds` array, not
 * `level.QualThresholds`. This is intentional — the popup always passes the quantitative
 * risk-level array. If a future caller passes qualitative-config levels, names would
 * silently return null; add a QualThresholds fallback (as in getColorForScore) at that point.
 */
export function getRiskLevelName(riskRating: number, riskLevels: RiskLevel[]): string | null {
    if (!riskLevels?.length || !Number.isFinite(riskRating) || riskRating === 0) {
        return null;
    }

    const index = findMatchingLevelIndex(riskRating, riskLevels, (level) => level.Thresholds);
    return index === -1 ? null : riskLevels[index].Name ?? null;
}

export function getLikelihoodColor(
    likelihoodScore: number,
    exposureLikelihoodLevels: LikelihoodColorLevel[],
): string {
    if (!exposureLikelihoodLevels?.length || !Number.isFinite(likelihoodScore)) {
        return DEFAULT_SCORE_COLOR;
    }

    const match = exposureLikelihoodLevels.find((level) => level.key === likelihoodScore);
    return match?.Color ?? DEFAULT_SCORE_COLOR;
}

/** Resolved exposure band for a likelihood score: the band word plus its % OEL range. */
export interface LikelihoodBand {
    /** Band word, e.g. "High" — `Name` for qualitative cells, `QuantitativeName` for quantitative. */
    name: string;
    /** Lower bound of the band's % OEL range, or null when the level carries no range. */
    minPercent: number | null;
    /** Upper bound of the band's % OEL range, or null when the level carries no range. */
    maxPercent: number | null;
}

/**
 * Resolves the exposure band a likelihood score falls into, using the SAME exact-key match as
 * {@link getLikelihoodColor} (`level.key === score`). Returns null when there is no valid score,
 * no matching level, or the matched level carries no band name — so callers can render a clear
 * "Not assessed" fallback instead of a misleading value.
 *
 * NOTE: `QuantThreshold_OEL` (`minQuantThreshold`/`maxQuantThreshold`) and `QualThresholds` hang
 * off the SAME level key, so a qualitative cell's resolved level still carries the % OEL range.
 * The label therefore works for qualitative cells too when resolved off `qualitativeLikelihoodScore`.
 *
 * `isQualitative` selects the band-word source for parity with the RA register: qualitative cells
 * resolve `Name`, quantitative cells resolve `QuantitativeName`, so the same SEG/agent pair cannot
 * read different band words on the two grids.
 */
export function getLikelihoodBand(
    likelihoodScore: number,
    exposureLikelihoodLevels: LikelihoodColorLevel[],
    isQualitative = false,
): LikelihoodBand | null {
    if (!exposureLikelihoodLevels?.length || !Number.isFinite(likelihoodScore)) {
        return null;
    }

    const match = exposureLikelihoodLevels.find((level) => level.key === likelihoodScore);
    if (!match) return null;

    // `||` (not `??`): the unused name field persists as '' (not null), so it must fall through to
    // the populated one instead of returning "Not assessed" for every assessed cell.
    const name = (isQualitative
        ? match.Name || match.QuantitativeName
        : match.QuantitativeName || match.Name)?.trim();
    if (!name) return null;

    return {
        name,
        minPercent: Number.isFinite(match.minQuantThreshold as number) ? (match.minQuantThreshold as number) : null,
        maxPercent: Number.isFinite(match.maxQuantThreshold as number) ? (match.maxQuantThreshold as number) : null,
    };
}

/** Formats a % OEL bound, trimming trailing zeros (e.g. 20, 40, 12.5). */
function formatPercent(value: number): string {
    return Number(value.toFixed(2)).toString();
}

/**
 * Composes the human-readable exposure band label for a likelihood score, e.g. "High (20-40% OEL)".
 * Returns just the band word when the level carries no % OEL range, or null when nothing matches
 * (caller renders "Not assessed").
 *
 * NOTE: Currently consumed ONLY by the Sampling Plan detail matrix
 * (`sample-planning-detail.component.ts`). The RA register still runs its own band-word
 * resolution and does NOT call this helper yet, so the two grids can still drift. Routing the
 * register through this helper is tracked as de-drift tech debt (DASH-2718); when that happens,
 * update this note to state both grids consume it.
 */
export function getLikelihoodBandLabel(
    likelihoodScore: number,
    exposureLikelihoodLevels: LikelihoodColorLevel[],
    isQualitative = false,
): string | null {
    const band = getLikelihoodBand(likelihoodScore, exposureLikelihoodLevels, isQualitative);
    if (!band) return null;

    // Only append a real ascending interval: a qualitatively-configured level persists [0, 0] and an
    // open-ended top band parses to [100, 0] (Number(null) === 0) — both would misrender as a range.
    if (band.minPercent !== null && band.maxPercent !== null && band.maxPercent > band.minPercent) {
        return `${band.name} (${formatPercent(band.minPercent)}-${formatPercent(band.maxPercent)}% OEL)`;
    }

    return band.name;
}

